import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiMiniBellAlert } from 'react-icons/hi2';
import { BsInfoCircle } from 'react-icons/bs';
import { HiLink, HiKey, HiPlusCircle, HiOutlineClipboardDocument } from 'react-icons/hi2';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { SessionData } from './types';
import ProjectSelector, { ProjectSelectorHandle } from './ProjectSelector';
import api from '../../../api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createRoomSchema, CreateRoomFormData } from './validation/createRoomSchema';

type TeamJoinMethod = 'link' | 'code' | 'create';

interface TeamModeContentProps {
  sessionData: SessionData;
  setSessionData: React.Dispatch<React.SetStateAction<SessionData>>;
  teamTab: 'create' | 'join';
  setTeamTab: React.Dispatch<React.SetStateAction<'create' | 'join'>>;
  roomCode: string;
  setRoomCode: React.Dispatch<React.SetStateAction<string>>;
  onCreateRoom: (data: CreateRoomFormData) => void;
  onJoinRoom: (roomCode?: string) => void;
  onManageProjects?: () => void;
  onCreateProjects?: () => void;
  projectSelectorRef?: React.RefObject<ProjectSelectorHandle | null>;
}



/** Extract room code from invite link (e.g. ?room=ABC123 or /room/ABC123) */
const extractRoomCodeFromLink = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const url = new URL(trimmed);
      const room = url.searchParams.get('room') || url.searchParams.get('roomId') || url.searchParams.get('code');
      if (room) return room.toUpperCase();
    }

    // Check path like /room/ABC123, /join/ABC123, or /team/ABC-123 without http
    const pathMatch = trimmed.match(/\/(?:room|join|team)\/([A-Za-z0-9\-]+)(?:[/?#]|$)/i);
    if (pathMatch) return pathMatch[1];
  } catch {
    // Ignore parse errors, return null below
  }

  return null;
};

const TeamModeContent: React.FC<TeamModeContentProps> = ({
  sessionData,
  setSessionData,
  teamTab,
  setTeamTab,
  roomCode,
  setRoomCode,
  onCreateRoom,
  onJoinRoom,
  onManageProjects,
  onCreateProjects,
  projectSelectorRef,
}) => {
  const navigate = useNavigate();
  const [joinMethod, setJoinMethod] = useState<TeamJoinMethod>('link');
  const [joinInput, setJoinInput] = useState('');
  const [linkError, setLinkError] = useState('');
  const hasInitializedJoinFromParent = React.useRef(false);

  // Form for creating a room
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateRoomFormData>({
    resolver: zodResolver(createRoomSchema),
    mode: 'onSubmit',
    defaultValues: {
      goal: sessionData.goal || '',
      projectId: sessionData.projectId || '',
      roomCode: sessionData.roomCode || '',
    },
  });

  const watchedRoomCode = watch('roomCode');
  const watchedProjectId = watch('projectId');

  const onFormSubmit = (data: CreateRoomFormData) => {
    onCreateRoom(data);
  };

  const onFormError = (_errs: any) => {
    // Validation errors are displayed inline via the errors object
  };

  // Prefill join input when coming from invite link (parent's roomCode set from URL)
  useEffect(() => {
    if (teamTab === 'join' && roomCode && !hasInitializedJoinFromParent.current) {
      setJoinInput(roomCode);
      hasInitializedJoinFromParent.current = true;
    }
  }, [teamTab, roomCode]);

  // Pre-generate room code when on Create tab so user can share before creating
  useEffect(() => {
    const fetchRoomCode = async () => {
      try {
        const response = await api.focus.generateTeamRoomId();
        setValue('roomCode', response.roomId);
        setSessionData(prev => ({ ...prev, roomCode: response.roomId }));
      } catch (error) {
        console.error('Failed to pre-generate room code:', error);
      }
    };

    if (teamTab === 'create' && !watchedRoomCode) {
      fetchRoomCode();
    }
  }, [teamTab, watchedRoomCode, setValue, setSessionData]);



  const handleJoinRoomSubmit = () => {
    const code = joinMethod === 'link'
      ? extractRoomCodeFromLink(joinInput)
      : joinInput.trim();
    if (code) {
      setRoomCode(code);
      onJoinRoom(code);
      navigate(`/team/${code}`);
    } else {
      alert(joinMethod === 'link'
        ? 'Please paste a valid invite link'
        : 'Please enter a room code (4–12 characters)');
    }
  };

  const canJoin = joinMethod === 'link'
    ? extractRoomCodeFromLink(joinInput) !== null
    : joinInput.trim().length >= 4;

  return (
    <div className="w-full min-h-screen bg-[#FFF8F0] pt-2 sm:pt-4 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-4xl sm:text-5xl font-medium text-gray-900">
            Team Focus Room
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 font-light">
            Focus together, achieve more — like a clubhouse
          </p>
        </div>

        {/* Join / Create Tabs */}
        <div className="flex justify-center">
          <div className="bg-white border border-gray-200/60 rounded-2xl p-1.5 inline-flex shadow-sm">
            <button
              onClick={() => setTeamTab('join')}
              className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${teamTab === 'join'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Join Room
            </button>
            <button
              onClick={() => setTeamTab('create')}
              className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base ${teamTab === 'create'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Create Room
            </button>
          </div>
        </div>

        {/* Join Room Section */}
        {teamTab === 'join' && (
          <div className="bg-white border border-gray-200/60 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
            <p className="text-gray-600 text-center">
              Join via invite link shared by someone, or enter the room code directly
            </p>

            {/* Join method toggle */}
            <div className="flex gap-2 p-1 bg-gray-50 rounded-xl">
              <button
                onClick={() => setJoinMethod('link')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all ${joinMethod === 'link'
                  ? 'bg-white text-orange-600 shadow-sm border border-orange-200/60'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <HiLink className="text-base" />
                Via Link
              </button>
              <button
                onClick={() => setJoinMethod('code')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all ${joinMethod === 'code'
                  ? 'bg-white text-orange-600 shadow-sm border border-orange-200/60'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <HiKey className="text-base" />
                Room ID
              </button>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {joinMethod === 'link'
                  ? 'Paste invite link'
                  : 'Enter room code'}
              </label>
              <input
                type="text"
                value={joinInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setJoinInput(val);
                  if (joinMethod === 'link' && val.trim() !== '') {
                    const linkRegex = /^(?:https?:\/\/)?(?:www\.)?[^\/]+\/team\/[a-zA-Z0-9\-]+$/;
                    if (!linkRegex.test(val.trim())) {
                      setLinkError(`Invalid link format. Expected: ${window.location.origin}/team/<room-id>`);
                    } else {
                      setLinkError('');
                    }
                  } else {
                    setLinkError('');
                  }
                }}
                placeholder={joinMethod === 'link'
                  ? `${typeof window !== 'undefined' ? window.location.origin : 'https://app.example.com'}/team/<room-id>`
                  : 'e.g. ABC123'}
                className={`w-full px-5 py-4 bg-white border rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:outline-none transition-all text-base ${linkError && joinMethod === 'link'
                  ? 'border-red-500 focus:ring-red-400/30 focus:border-red-500'
                  : 'border-gray-200/60 focus:ring-orange-400/30 focus:border-orange-300/50'
                  }`}
              />
              {linkError && joinMethod === 'link' && (
                <p className="text-red-500 text-sm mt-1">{linkError}</p>
              )}
            </div>

            <button
              onClick={handleJoinRoomSubmit}
              disabled={!canJoin || (joinMethod === 'link' && !!linkError)}
              className={`w-full py-4 rounded-xl font-medium text-base transition-all ${canJoin && !(joinMethod === 'link' && !!linkError)
                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
            >
              Join Focus Room
            </button>
          </div>
        )}

        {/* Create Room Section - No focus sounds */}
        {teamTab === 'create' && (
          <div className="space-y-8">
            <form onSubmit={handleSubmit(onFormSubmit, onFormError)} className="bg-white border border-gray-200/60 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">

              {Object.keys(errors).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-600 text-sm font-medium">Please fix the following errors:</p>
                  <ul className="text-red-600 text-sm mt-2 list-disc list-inside">
                    {Object.entries(errors).map(([key, value]) => (
                      <li key={key}>{value?.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What will you focus on? */}
              <div className="space-y-2">
                <label className="block text-lg text-gray-700 font-normal">
                  What will you focus on?
                </label>
                <input
                  type="text"
                  {...register('goal')}
                  placeholder="e.g. Finish today's top task, Prepare meeting notes"
                  className={`w-full px-5 py-4 bg-white border rounded-xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:outline-none transition-all text-base ${errors.goal
                    ? 'border-red-500 focus:ring-red-400/30 focus:border-red-500'
                    : 'border-gray-200/60 focus:ring-orange-400/30 focus:border-orange-300/50'
                    }`}
                />
                {errors.goal && (
                  <p className="text-red-500 text-sm">{errors.goal.message}</p>
                )}
              </div>

              {/* Focus Area */}
              <div className="space-y-2">
                <ProjectSelector
                  ref={projectSelectorRef}
                  selectedProjectId={watchedProjectId}
                  onProjectChange={(projectId) => {
                    if (projectId) {
                      setValue('projectId', projectId, { shouldValidate: true });
                    }
                  }}
                  showManageButton={true}
                  onManageClick={onManageProjects}
                  onCreateClick={onCreateProjects}
                  required={true}
                />
                {errors.projectId && (
                  <p className="text-red-500 text-sm">{errors.projectId.message}</p>
                )}
              </div>

              {/* Share with team - Room ID & Invite Link */}
              {watchedRoomCode && (
                <div className="bg-orange-50/50 border border-orange-200/40 rounded-2xl p-5 space-y-4">
                  <h4 className="text-base font-medium text-gray-900 flex items-center gap-2">
                    <HiOutlineClipboardDocument className="text-orange-500" />
                    Share with your team
                  </h4>
                  <p className="text-sm text-gray-600">
                    Share this room ID or invite link so others can join before you start.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Room ID:</span>
                      <code className="flex-1 px-3 py-2 bg-white border border-gray-200/60 rounded-lg text-sm font-mono text-gray-900 truncate">
                        {watchedRoomCode}
                      </code>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(watchedRoomCode);
                          alert('Room ID copied!');
                        }}
                        className="px-3 py-2 bg-white border border-orange-200/60 hover:bg-orange-50 text-orange-600 rounded-lg text-sm font-medium shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          const link = `${typeof window !== 'undefined' ? window.location.origin : ''}/team/${watchedRoomCode}`;
                          navigator.clipboard.writeText(link);
                          alert('Invite link copied!');
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-orange-200/60 hover:bg-orange-50 text-orange-600 rounded-lg text-sm font-medium"
                      >
                        <HiLink className="text-base shrink-0" />
                        Copy Invite Link
                      </button>
                    </div>
                  </div>
                </div>
              )}



              {/* CTA - Create Room */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 rounded-2xl font-medium text-lg transition-all duration-300 flex items-center justify-center gap-2 ${isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
              >
                <HiPlusCircle className="text-xl" />
                {isSubmitting ? 'Creating...' : 'Create Focus Room'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamModeContent;
